/**
 * 服务协议服务（GET /v1/agreement）
 *
 * 用于拉取平台各类服务协议全文，当前用于 Skill 发布弹窗内的
 * 《AmeAI创作许可服务协议》弹层（需求 §3.5.4）。
 *
 * 协议为低频只读内容，故在模块级做进程内缓存：同一 agreement_id
 * 只请求一次，避免用户反复打开弹层时重复打网络。
 */
import { get } from '@/utils';
import type { ApiResponse } from '@/utils';

/** 协议详情（GET /v1/agreement 返回 data） */
export interface AgreementDetail {
  /** 协议标题 */
  title: string;
  /** 发布日期 */
  publish_date: string;
  /** 生效日期 */
  effective_date: string;
  /** 协议正文（Markdown / 纯文本） */
  content: string;
}

class AgreementService {
  /** agreement_id → 协议详情（成功结果才入缓存） */
  private cache = new Map<string, AgreementDetail>();
  /** agreement_id → 进行中的请求（并发去重，防止弹层连开打多次） */
  private inflight = new Map<string, Promise<ApiResponse<AgreementDetail>>>();

  /**
   * 查询协议全文。
   * @param agreementId 协议 id，如 CREATOR_LISCENCE（Ame AI 创作许可服务协议）
   * @param force 跳过缓存强制重新拉取（加载失败后「重新加载」用）
   */
  async getAgreement(agreementId: string, force = false): Promise<ApiResponse<AgreementDetail>> {
    if (!force) {
      const cached = this.cache.get(agreementId);
      if (cached) return { code: 0, data: cached, message: '' };
      const pending = this.inflight.get(agreementId);
      if (pending) return pending;
    }

    const task = get<AgreementDetail>('/agreement', { agreement_id: agreementId })
      .then(res => {
        if (res.code === 0 && res.data) this.cache.set(agreementId, res.data);
        return res;
      })
      .finally(() => {
        this.inflight.delete(agreementId);
      });

    this.inflight.set(agreementId, task);
    return task;
  }

  /** 清空缓存（协议文本线上更新后可主动失效，便于调试） */
  clearCache(agreementId?: string): void {
    if (agreementId) this.cache.delete(agreementId);
    else this.cache.clear();
  }
}

export default new AgreementService();
